import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Link } from "react-router-dom";

const RegisterPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (password !== repeatPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      await axios.post(`${SERVER_URL}/api/auth/users/`, {
        username,
        email,
        password,
        re_password: repeatPassword,
        first_name: firstName || "",
        last_name: lastName || "",
      });
      setSuccess(
        "Registration successful! Please check your email to activate your account."
      );
    } catch (err) {
      const errors = err.response?.data || { detail: "Registration failed." };
      if (errors.username) {
        setError(errors.username[0]);
      } else if (errors.non_field_errors) {
        setError(errors.non_field_errors[0]);
      } else if (errors.email) {
        setError(errors.email[0]);
      } else {
        setError(errors.detail || "An error occurred. Please try again.");
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 sm:px-0">
      <Helmet>
        <title>Register – LingoLab</title>
        <meta
          name="description"
          content="Create your LingoLab account to access AI-powered video translation and localization services. Fast, secure registration."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://lingolab.ca/register" />
        <meta property="og:title" content="Register – LingoLab" />
        <meta
          property="og:description"
          content="Create your LingoLab account to access AI-powered video translation and localization services."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lingolab.ca/register" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingLabAI" />
        <meta name="twitter:title" content="Register – LingoLab" />
        <meta
          name="twitter:description"
          content="Create your LingoLab account to access AI-powered video translation and localization services."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg w-full max-w-full sm:max-w-md">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">
          Register
        </h2>
        <form onSubmit={handleRegister}>
          {/* ...existing form fields... */}
          <div className="mb-3 sm:mb-4">
            <label
              htmlFor="username"
              className="block text-gray-700 font-semibold mb-1 sm:mb-2"
            >
              Username <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
              autoFocus
              autoComplete="username"
              autoCapitalize="none"
            />
          </div>
          {/* ...rest of your form fields and logic... */}
          <div className="mb-3 sm:mb-4">
            <label
              htmlFor="email"
              className="block text-gray-700 font-semibold mb-1 sm:mb-2"
            >
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
              autoComplete="email"
            />
          </div>
          <div className="mb-3 sm:mb-4">
            <label
              htmlFor="password"
              className="block text-gray-700 font-semibold mb-1 sm:mb-2"
            >
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
              autoComplete="new-password"
            />
          </div>
          <div className="mb-3 sm:mb-4">
            <label
              htmlFor="repeatPassword"
              className="block text-gray-700 font-semibold mb-1 sm:mb-2"
            >
              Repeat Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              id="repeatPassword"
              value={repeatPassword}
              onChange={(e) => setRepeatPassword(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
              autoComplete="new-password"
            />
          </div>
          <div className="mb-3 sm:mb-4">
            <label
              htmlFor="firstName"
              className="block text-gray-700 font-semibold mb-1 sm:mb-2"
            >
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
              autoComplete="given-name"
            />
          </div>
          <div className="mb-3 sm:mb-4">
            <label
              htmlFor="lastName"
              className="block text-gray-700 font-semibold mb-1 sm:mb-2"
            >
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              autoComplete="family-name"
            />
          </div>
          {error && (
            <p className="text-red-500 mb-3 sm:mb-4 text-sm sm:text-base break-words">
              {error}
            </p>
          )}
          {success && (
            <p className="text-green-500 mb-3 sm:mb-4 text-sm sm:text-base break-words">
              {success}
            </p>
          )}
          <button
            type="submit"
            className="w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-700 text-base"
          >
            Register
          </button>
        </form>
        <div className="mt-3 sm:mt-4 text-center">
          <p className="text-gray-600 text-sm sm:text-base">
            Already have an account?{" "}
            <Link
              to="/signin"
              className="text-blue-500 hover:underline font-semibold"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
